import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ShieldCheck, Package, RotateCcw, AlertCircle, Info, Lock,
} from "lucide-react";
import SEO from "@/components/SEO";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/formatGBP";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

const initialAddress = () => ({
  line1: "", line2: "", city: "", postcode: "", country: "GB",
});

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotalPennies, validateWithServer } = useCart();

  const params = new URLSearchParams(window.location.search);
  const wasCancelled = params.get("cancelled") === "1";

  const [contact, setContact] = useState({ email: "", marketing_opt_in: false });
  const [customer, setCustomer] = useState({ first_name: "", last_name: "", phone: "" });
  const [shipping, setShipping] = useState(initialAddress());
  const [billing, setBilling] = useState(initialAddress());
  const [sameAsShipping, setSameAsShipping] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Guard: empty cart → /cart
  useEffect(() => {
    if (items.length === 0) {
      navigate("/cart", { replace: true });
    } else {
      validateWithServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = subtotalPennies;

  const validate = () => {
    const errs = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) errs.email = "Enter a valid email.";
    if (!customer.first_name.trim()) errs.first_name = "Required";
    if (!customer.last_name.trim()) errs.last_name = "Required";
    if (!shipping.line1.trim()) errs.s_line1 = "Required";
    if (!shipping.city.trim()) errs.s_city = "Required";
    if (!UK_POSTCODE_RE.test(shipping.postcode.trim()))
      errs.s_postcode = "Enter a valid UK postcode (e.g. SW1A 1AA).";
    if (shipping.country !== "GB") errs.s_country = "We only ship to the UK currently.";
    if (!sameAsShipping) {
      if (!billing.line1.trim()) errs.b_line1 = "Required";
      if (!billing.city.trim()) errs.b_city = "Required";
      if (!UK_POSTCODE_RE.test(billing.postcode.trim()))
        errs.b_postcode = "Enter a valid UK postcode.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTopError("");
    if (!validate()) {
      // Scroll to first error
      window.scrollTo({ top: 200, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        customer: {
          email: contact.email.trim().toLowerCase(),
          first_name: customer.first_name.trim(),
          last_name: customer.last_name.trim(),
          phone: customer.phone.trim(),
        },
        shipping_address: {
          line1: shipping.line1.trim(),
          line2: shipping.line2.trim(),
          city: shipping.city.trim(),
          postcode: shipping.postcode.trim().toUpperCase(),
          country: "GB",
        },
        billing_address: sameAsShipping
          ? {
              line1: shipping.line1.trim(),
              line2: shipping.line2.trim(),
              city: shipping.city.trim(),
              postcode: shipping.postcode.trim().toUpperCase(),
              country: "GB",
            }
          : {
              line1: billing.line1.trim(),
              line2: billing.line2.trim(),
              city: billing.city.trim(),
              postcode: billing.postcode.trim().toUpperCase(),
              country: "GB",
            },
        origin_url: window.location.origin,
      };

      // Best-effort: also subscribe to newsletter if opted in (fire-and-forget)
      if (contact.marketing_opt_in) {
        axios.post(`${API}/newsletter`, {
          email: body.customer.email, source: "checkout",
        }).catch(() => {});
      }

      const res = await axios.post(`${API}/checkout/create-session`, body);
      if (res.data?.session_url) {
        window.location.href = res.data.session_url;
        return;
      }
      setTopError("Couldn't start checkout — please try again.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Couldn't start checkout — please try again.";
      setTopError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Checkout | PeptiChill"
        description="Complete your PeptiChill pre-order. Ships July 2026, UK tracked delivery."
        path="/checkout"
      />
      <section className="bg-ice-light py-10 lg:py-16 min-h-[70vh]" data-testid="page-checkout">
        <div className="container-pc">
          <p className="eyebrow">Secure checkout</p>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tightest leading-[1.05]">
            Checkout
          </h1>

          {wasCancelled && (
            <div
              className="mt-6 flex items-start gap-3 rounded-2xl bg-white border border-silver/60 shadow-card p-4"
              data-testid="checkout-cancelled-banner"
              role="status"
            >
              <Info size={18} className="mt-0.5 text-cyan shrink-0" />
              <p className="text-sm text-ink">
                Payment cancelled — your basket is still saved. Try again when ready.
              </p>
            </div>
          )}

          {/* Pre-order info banner */}
          <div
            className="mt-6 flex items-start gap-3 rounded-2xl bg-ice border border-cyan/30 p-4"
            data-testid="checkout-preorder-banner"
            role="status"
          >
            <Info size={18} className="mt-0.5 text-navy shrink-0" />
            <p className="text-sm text-navy">
              You&rsquo;re placing a pre-order. PeptiChill ships from July 2026.
            </p>
          </div>

          {topError && (
            <div
              className="mt-6 flex items-start gap-3 rounded-2xl bg-destructive/5 border border-destructive/20 p-4"
              data-testid="checkout-error-banner"
              role="alert"
            >
              <AlertCircle size={18} className="mt-0.5 text-destructive shrink-0" />
              <p className="text-sm text-destructive">{topError}</p>
            </div>
          )}

          <form
            onSubmit={onSubmit}
            noValidate
            className="mt-8 grid lg:grid-cols-[1.3fr,1fr] gap-8 lg:gap-12 items-start"
            data-testid="checkout-form"
          >
            <div className="space-y-6">
              {/* Contact */}
              <Card title="Contact" testId="checkout-section-contact">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={contact.email}
                  required
                  error={fieldErrors.email}
                  onChange={(v) => setContact((p) => ({ ...p, email: v }))}
                  testId="checkout-email"
                  autoComplete="email"
                  inputMode="email"
                />
                <label className="mt-3 flex items-center gap-2 text-sm text-ink/80">
                  <input
                    type="checkbox"
                    checked={contact.marketing_opt_in}
                    onChange={(e) => setContact((p) => ({ ...p, marketing_opt_in: e.target.checked }))}
                    className="h-4 w-4 rounded border-silver text-navy focus:ring-cyan"
                    data-testid="checkout-marketing-opt-in"
                  />
                  Email me clinical storage updates. No spam.
                </label>
              </Card>

              {/* Shipping */}
              <Card title="Shipping address" testId="checkout-section-shipping">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="First name" name="first_name" value={customer.first_name}
                    required error={fieldErrors.first_name}
                    onChange={(v) => setCustomer((p) => ({ ...p, first_name: v }))}
                    testId="checkout-first-name"
                    autoComplete="given-name"
                  />
                  <Field
                    label="Last name" name="last_name" value={customer.last_name}
                    required error={fieldErrors.last_name}
                    onChange={(v) => setCustomer((p) => ({ ...p, last_name: v }))}
                    testId="checkout-last-name"
                    autoComplete="family-name"
                  />
                </div>
                <Field
                  label="Address line 1" name="s_line1" value={shipping.line1}
                  required error={fieldErrors.s_line1}
                  onChange={(v) => setShipping((p) => ({ ...p, line1: v }))}
                  testId="checkout-shipping-line1"
                  autoComplete="address-line1"
                />
                <Field
                  label="Address line 2 (optional)" name="s_line2" value={shipping.line2}
                  onChange={(v) => setShipping((p) => ({ ...p, line2: v }))}
                  testId="checkout-shipping-line2"
                  autoComplete="address-line2"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="City" name="s_city" value={shipping.city}
                    required error={fieldErrors.s_city}
                    onChange={(v) => setShipping((p) => ({ ...p, city: v }))}
                    testId="checkout-shipping-city"
                    autoComplete="address-level2"
                  />
                  <Field
                    label="Postcode" name="s_postcode" value={shipping.postcode}
                    required error={fieldErrors.s_postcode}
                    onChange={(v) => setShipping((p) => ({ ...p, postcode: v.toUpperCase() }))}
                    testId="checkout-shipping-postcode"
                    placeholder="SW1A 1AA"
                    autoComplete="postal-code"
                    autoCapitalize="characters"
                    inputMode="text"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted mb-2">
                    Country
                  </label>
                  <select
                    value={shipping.country}
                    onChange={(e) => setShipping((p) => ({ ...p, country: e.target.value }))}
                    data-testid="checkout-shipping-country"
                    autoComplete="country"
                    className="w-full h-12 sm:h-11 rounded-xl border border-silver/70 bg-white px-4 text-base sm:text-sm text-ink focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition"
                  >
                    <option value="GB">United Kingdom</option>
                  </select>
                  {fieldErrors.s_country && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.s_country}</p>
                  )}
                </div>
                <Field
                  label="Phone (optional)" name="phone" value={customer.phone}
                  onChange={(v) => setCustomer((p) => ({ ...p, phone: v }))}
                  testId="checkout-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </Card>

              {/* Billing */}
              <Card title="Billing address" testId="checkout-section-billing">
                <label className="flex items-center gap-2 text-sm text-ink/80">
                  <input
                    type="checkbox"
                    checked={sameAsShipping}
                    onChange={(e) => setSameAsShipping(e.target.checked)}
                    className="h-4 w-4 rounded border-silver text-navy focus:ring-cyan"
                    data-testid="checkout-billing-same"
                  />
                  Same as shipping address
                </label>
                {!sameAsShipping && (
                  <div className="mt-4 space-y-4">
                    <Field
                      label="Address line 1" name="b_line1" value={billing.line1}
                      required error={fieldErrors.b_line1}
                      onChange={(v) => setBilling((p) => ({ ...p, line1: v }))}
                      testId="checkout-billing-line1"
                    />
                    <Field
                      label="Address line 2 (optional)" name="b_line2" value={billing.line2}
                      onChange={(v) => setBilling((p) => ({ ...p, line2: v }))}
                      testId="checkout-billing-line2"
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field
                        label="City" name="b_city" value={billing.city}
                        required error={fieldErrors.b_city}
                        onChange={(v) => setBilling((p) => ({ ...p, city: v }))}
                        testId="checkout-billing-city"
                      />
                      <Field
                        label="Postcode" name="b_postcode" value={billing.postcode}
                        required error={fieldErrors.b_postcode}
                        onChange={(v) => setBilling((p) => ({ ...p, postcode: v.toUpperCase() }))}
                        testId="checkout-billing-postcode"
                      />
                    </div>
                  </div>
                )}
              </Card>

              {/* Payment notice */}
              <Card title="Payment" testId="checkout-section-payment">
                <p className="text-sm text-ink/80 leading-relaxed">
                  After clicking <strong>Place pre-order · {formatGBP(total)}</strong>, you&rsquo;ll be
                  securely redirected to Stripe to complete your payment with card, Apple Pay or
                  Google Pay.
                </p>
                <p className="mt-3 flex items-center gap-2 text-xs text-muted">
                  <Lock size={12} className="text-cyan" />
                  Encrypted &amp; PCI-compliant via Stripe
                </p>
              </Card>

              <button
                type="submit"
                disabled={submitting || items.length === 0}
                data-testid="checkout-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white h-14 text-base font-semibold tracking-wide hover:brightness-110 transition shadow-card disabled:opacity-70"
              >
                {submitting && <Loader2 size={18} className="animate-spin" />}
                {submitting ? "Redirecting to Stripe…" : `Place pre-order · ${formatGBP(total)}`}
              </button>
            </div>

            {/* Order summary */}
            <aside className="lg:sticky lg:top-24" data-testid="checkout-summary">
              <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-6">
                <h2 className="text-lg font-bold text-navy">Order summary</h2>
                <ul className="mt-5 space-y-4 divide-y divide-silver/40">
                  {items.map((it) => (
                    <li key={it.slug} className="flex gap-4 pt-4 first:pt-0">
                      <div className="w-14 h-14 rounded-xl overflow-hidden bg-ice-light border border-silver/40 shrink-0">
                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy leading-tight">{it.name}</p>
                        <p className="text-xs text-muted mt-0.5">Qty {it.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-navy tabular-nums">
                        {formatGBP(it.price_pennies * it.quantity)}
                      </p>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-silver/40 space-y-2 text-sm">
                  <div className="flex justify-between text-ink">
                    <span>Subtotal</span>
                    <span className="font-semibold tabular-nums">{formatGBP(subtotalPennies)}</span>
                  </div>
                  <div className="flex justify-between text-muted">
                    <span>UK delivery</span>
                    <span>Free</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-silver/40 flex justify-between items-center">
                  <span className="font-semibold text-navy">Total</span>
                  <span
                    className="text-2xl font-bold text-navy tabular-nums"
                    data-testid="checkout-total"
                  >
                    {formatGBP(total)}
                  </span>
                </div>
              </div>

              <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-muted">
                <li className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-cyan" /> Secure payment via Stripe
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Package size={14} className="text-cyan" /> UK tracked delivery
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <RotateCcw size={14} className="text-cyan" /> 14-day returns
                </li>
              </ul>
            </aside>
          </form>
        </div>
      </section>
    </>
  );
}

function Card({ title, children, testId }) {
  return (
    <div
      className="rounded-2xl bg-white border border-silver/40 shadow-card p-5 sm:p-6"
      data-testid={testId}
    >
      <h2 className="text-base font-bold text-navy mb-4 tracking-tight">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({
  label, name, type = "text", value, onChange, required, error, testId, placeholder,
  autoComplete, inputMode, autoCapitalize,
}) {
  return (
    <div>
      <label
        htmlFor={`f-${name}`}
        className="block text-xs uppercase tracking-widest text-muted mb-2"
      >
        {label}
        {required && <span className="text-cyan"> *</span>}
      </label>
      <input
        id={`f-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoCapitalize={autoCapitalize}
        data-testid={testId}
        className={`w-full h-12 sm:h-11 rounded-xl border bg-white px-4 text-base sm:text-sm text-ink placeholder:text-muted/70 focus:outline-none focus:ring-2 focus:ring-cyan focus:border-cyan transition ${
          error ? "border-destructive/60" : "border-silver/70"
        }`}
      />
      {error && (
        <p className="mt-1 text-xs text-destructive" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
